import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Geosuggest from 'react-geosuggest';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button, MultiSelect } from '@cargomatic/ui';
import { Pencil } from 'lucide-react';
const datepickerDefaultOptions = {
    showTimeSelect: false,
    dateFormat: 'L',
    isClearable: true,
    timeIntervals: 30,
    timeCaption: 'Time',
};
class ItemAlt extends Component {
    constructor(props) {
        super(props);
        this.handleCancel = () => {
            var _a;
            this.setState({ hover: false, editing: false });
            if ((_a = this.props) === null || _a === void 0 ? void 0 : _a.onValueCancel) {
                this.props.onValueCancel();
            }
        };
        this.handleAction = () => {
            this.setState({ editing: true });
        };
        this.state = {
            editing: false,
            value: this.props.value,
            name: this.props.name,
            hover: false,
        };
    }
    getEditComponent() {
        var _a, _b;
        let component;
        let renderMenuItems;
        let titleFunction;
        const datepickerOptions = Object.assign(Object.assign({}, datepickerDefaultOptions), this.props.datepickerOptions);
        const { showTimeSelect, dateFormat, isClearable, timeIntervals, timeCaption, } = datepickerOptions;
        const { localizationKey, t, disableTitleize = false } = this.props;
        switch (this.props.type) {
            case 'date':
                component = (React.createElement(DatePicker, { selected: mapDateValue(this.state.value), placeholderText: this.props.placeholderText, className: "w-full form-field", onChange: (val) => {
                        const dateString = val ? val.toISOString() : null;
                        this.handleValueChange(dateString);
                    }, autoFocus: true, showTimeSelect: showTimeSelect, dateFormat: dateFormat, isClearable: isClearable, timeIntervals: timeIntervals, timeCaption: timeCaption }));
                break;
            case 'address':
                component = (React.createElement(Geosuggest, { placeholder: t('SEARCH_PLACES'), inputClassName: this.props.inputClass || 'form-field', initialValue: this.props.value, onSuggestSelect: (item) => {
                        this.handleValueChange(item.gmaps.formatted_address);
                    } }));
                break;
            case 'currency':
                component = (React.createElement("input", { type: "number", min: "0", step: "0.10", className: this.props.inputClass || 'form-field', value: this.state.value || '', onChange: (e) => this.handleValueChange(e.target.value) }));
                break;
            case 'dropdown':
                titleFunction = (strValue) => disableTitleize ? t(strValue) : titleize(t(strValue));
                renderMenuItems = this.props.menuItems.map((serviceType) => (React.createElement("option", { key: serviceType.value, value: serviceType.value }, localizationKey &&
                    (localizationKey === null || localizationKey === void 0 ? void 0 : localizationKey.length) &&
                    Object.keys(serviceType).includes(localizationKey)
                    ? titleFunction(serviceType[localizationKey])
                    : serviceType.name)));
                component = (React.createElement("fieldset", { className: "" },
                    React.createElement("select", { onChange: (e) => this.handleValueChange(e.target.value), defaultValue: this.state.value, className: "form-select" }, renderMenuItems)));
                break;
            case 'dropdown_with_input':
                renderMenuItems = this.props.menuItems.map((serviceType) => (React.createElement("option", { key: serviceType.value, value: serviceType.value }, localizationKey &&
                    (localizationKey === null || localizationKey === void 0 ? void 0 : localizationKey.length) &&
                    Object.keys(serviceType).includes(localizationKey)
                    ? titleize(t(serviceType[localizationKey]))
                    : serviceType.name)));
                component = (React.createElement("fieldset", { className: "" },
                    React.createElement("select", { onChange: (e) => this.handleValueChange(e.target.value), defaultValue: this.state.value || this.props.displayValue, className: "form-select" }, renderMenuItems),
                    this.props.renderConditionalField &&
                        this.state.value === 'Custom' && (React.createElement("input", { type: "text", className: this.props.inputClass || 'form-field', style: { marginTop: 5, marginBottom: 5 }, value: this.state.custom_value, placeholder: this.props.inputPlaceholder || '', onChange: (e) => this.handleValueChange(e.target.value, 'custom_value') }))));
                break;
            case 'number':
                component = (React.createElement("input", { type: "number", className: this.props.inputClass || 'form-field', value: this.state.value || '', onChange: (e) => this.handleValueChange(e.target.value) }));
                break;
            case 'multi-select':
                component = (React.createElement(MultiSelect, { options: this.props.menuItems || [], value: ((_b = (_a = this.props) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.map((v) => ({ value: v, label: v }))) || [], placeholder: this.props.placeholderText, onChange: this.props.onChange, allowCustom: this.props.allowCustom, customOptionLabel: this.props.customOptionLabel, addCustomLabel: this.props.addCustomLabel, onCustomValueAdd: this.props.onCustomValueAdd }));
                break;
            default:
                // NOTE: Some components are passing in explicit types that fall into this default. See the Order Details component for examples. Type="string"
                component = (React.createElement("input", { type: "text", className: this.props.inputClass || 'form-field', value: this.state.value || '', onChange: (e) => this.handleValueChange(e.target.value), maxLength: this.props.maxLength }));
        }
        return component;
    }
    getDisplayComponent() {
        return (React.createElement("div", { className: this.props.displayClass || 'text-gray-500 text-gray-900 texxt-sm', "data-testid": "item-alt-value" }, this.props.displayValue || this.props.value));
    }
    getEditActions() {
        const { t } = this.props;
        return (React.createElement("div", { className: "flex flex-wrap mt-1 mb-4 gap-2" },
            React.createElement(Button, { size: "sm", variant: "default", onClick: () => this.handleValueSave(), disabled: this.props.type === 'date' && !this.state.value }, titleize(t('SAVE'))),
            React.createElement(Button, { size: "sm", variant: "outline", onClick: this.handleCancel }, titleize(t('CANCEL')))));
    }
    getDisplayAction() {
        if (!this.props.editable) {
            return null;
        }
        return (React.createElement(Button, { variant: "ghost", size: "icon", className: `h-6 w-6 shrink-0 ${this.state.hover ? 'opacity-100' : 'opacity-0'}`, onClick: this.handleAction, "data-testid": "editButton" },
            React.createElement(Pencil, { className: "h-3 w-3" })));
    }
    handleHover(hovered) {
        if (this.props.editable) {
            this.setState({ hover: hovered });
        }
    }
    handleValueChange(value, key) {
        if (!key) {
            this.setState({ value });
        }
        else {
            this.setState({ [key]: value });
        }
    }
    clearValues() {
        this.setState({
            custom_value: '',
            value: '',
        });
    }
    handleValueSave() {
        this.setState({ hover: false, editing: false }, () => {
            if (this.state.custom_value) {
                this.props.onValueSave({ [this.state.name]: this.state.custom_value });
            }
            else if (this.props.type === 'multi-select') {
                this.props.onValueSave({ [this.state.name]: this.props.value });
            }
            else {
                this.props.onValueSave({ [this.state.name]: this.state.value });
            }
            this.clearValues();
        });
    }
    render() {
        if (this.state.editing) {
            return (React.createElement("div", { className: this.props.rowClass },
                this.getEditComponent(),
                this.getEditActions()));
        }
        return (React.createElement("div", { className: this.props.rowClass || 'flex items-center min-h-6 space-x-2', onMouseEnter: () => this.handleHover(true), onMouseLeave: () => this.handleHover(false) },
            React.createElement("div", null, this.getDisplayComponent()),
            React.createElement("div", null, this.getDisplayAction())));
    }
}
ItemAlt.propTypes = {
    type: PropTypes.string,
    editable: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholderText: PropTypes.string,
    buttonContainerClass: PropTypes.string,
    defaultCancelClass: PropTypes.string,
    displayClass: PropTypes.string,
    displayValue: PropTypes.any,
    glyphClass: PropTypes.string,
    inputClass: PropTypes.string,
    rowClass: PropTypes.string,
    menuItems: PropTypes.array,
    cancelButtonClass: PropTypes.string,
    saveButtonClass: PropTypes.string,
    onValueSave: PropTypes.func,
    maxLength: PropTypes.number,
    datepickerOptions: PropTypes.shape({
        showTimeSelect: PropTypes.bool,
        dateFormat: PropTypes.string,
        isClearable: PropTypes.bool,
    }),
    renderConditionalField: PropTypes.bool,
    localizationKey: PropTypes.string,
};
ItemAlt.defaultProps = {
    datepickerOptions: Object.assign({}, datepickerDefaultOptions),
    renderConditionalField: true,
};
function mapDateValue(value) {
    if (!value) {
        return null;
    }
    if (value instanceof moment) {
        return value;
    }
    return moment(value);
}
export default withTranslation()(ItemAlt);
